import React, {
  createContext,
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Trans } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';
import {
  type EntitlementsProject,
  useAlloyFeatureFlag,
} from '@adsk/alloy-provider';
import { LinkButton } from '@adsk/alloy-react-button';
import { useContextWithValidation } from '@adsk/alloy-react-context';
import { useLocalState } from '@adsk/alloy-react-helpers';
import { useNotifications } from '@adsk/alloy-react-notification';

import {
  FEATURE_FLAGS,
  LOCAL_STORAGE_KEYS,
  SuccessNotificationBaseProps,
  ErrorNotificationBaseProps,
  getUserPreferenceV2Url,
  getUserPreferenceUrl,
  putUserPreferenceUrl,
} from '../../constants';
import i18n from '../../helpers/i18n';

type ProductEntryFlowResponse = {
  data: ProductEntryFlowData[];
};

type ProductEntryFlowData = {
  id: string;
  value: string;
};

type ProductEntryFlowValue = Record<string, string> | undefined;

type ProductEntryFlowContext = {
  loading?: boolean;
  setLoading: (loading: boolean) => void;
  selectedProductIds: ProductEntryFlowValue;
  storeProductSelection: (
    project: EntitlementsProject,
    productId: string,
    productName: string,
  ) => void;
  loadProductSelections: (data: ProductEntryFlowData[]) => void;
} | null;

const ProductEntryFlowContext = createContext<ProductEntryFlowContext>(null);

export const useProductEntryFlowContext = () =>
  useContextWithValidation(ProductEntryFlowContext, {
    hook: 'useProductEntryFlowContext',
    provider: 'ProductEntryFlowContext',
  });

type ProductEntryFlowContextProps = {
  children: ReactNode;
};

export const useProductEntryFlow = () => {
  return useProductEntryFlowContext();
};

export function useSetProductEntryFlow(projects?: EntitlementsProject[]) {
  const { showNotification } = useNotifications();
  const { setLoading, loadProductSelections } = useProductEntryFlowContext();
  const {
    value: enableTableProductEntryFlow,
    loading: loadingTableProductEntryFlowFF,
  } = useAlloyFeatureFlag(FEATURE_FLAGS.EnableTableProductEntryFlow);
  const { value: userPreferenceV2, loading: loadingUserPreferencesV2FF } =
    useAlloyFeatureFlag(FEATURE_FLAGS.UserPreferencesV2);

  const loadingFF = useMemo(
    () => loadingTableProductEntryFlowFF || loadingUserPreferencesV2FF,
    [loadingTableProductEntryFlowFF, loadingUserPreferencesV2FF],
  );

  const { mutate, isLoading, isValidating } =
    useSWRImmutable<ProductEntryFlowResponse>(
      !loadingFF &&
        enableTableProductEntryFlow &&
        !!projects &&
        projects.length &&
        `${
          userPreferenceV2 ? getUserPreferenceV2Url() : getUserPreferenceUrl()
        }?${projects
          .map((project: EntitlementsProject) => `ids=${project.id}`)
          .join('&')}`,
      {
        fetcher: fetchWithTokenAndRegion,
        revalidateOnMount: true,
        onDiscarded: () => {
          setLoading(false);
        },
        onSuccess: (data) => {
          loadProductSelections(data?.data?.filter((d) => d.value)); // filter out empty values
          setLoading(false);
        },
        onError: (error) => {
          console.error(error);
          setLoading(false);

          showNotification({
            ...ErrorNotificationBaseProps,
            message: (
              <span>
                <Trans
                  i18nKey="PRODUCT_ENTRY_LOADING_ERROR_NOTIFICATION"
                  values={{
                    retry: `<RetryLink>${i18n.t('RETRY')}</RetryLink>`,
                  }}
                  components={{
                    RetryLink: (
                      <LinkButton
                        onClick={() => {
                          mutate();
                        }}
                      />
                    ),
                  }}
                />
              </span>
            ),
          });
        },
      },
    );

  useEffect(() => {
    if (isLoading || isValidating) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoading, isValidating, setLoading]);
}

export const ProductEntryFlowContextProvider: FC<
  ProductEntryFlowContextProps
> = ({ children }) => {
  const { showNotification } = useNotifications();
  const { value: enableTableProductEntryFlow } = useAlloyFeatureFlag(
    FEATURE_FLAGS.EnableTableProductEntryFlow,
    false,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProductIds, setSelectedProductIds] =
    useLocalState<ProductEntryFlowValue>(
      LOCAL_STORAGE_KEYS.TableProductEntryFlow,
      {},
    );

  const value = useMemo(
    () => ({
      loading,
      setLoading,
      selectedProductIds: enableTableProductEntryFlow ? selectedProductIds : {},
      storeProductSelection: async (
        project: EntitlementsProject,
        productId: string,
        productName: string,
      ) => {
        if (!enableTableProductEntryFlow) {
          return;
        }

        const finalSelectedProductIds = { ...selectedProductIds };
        finalSelectedProductIds[project.id] = productId;
        setSelectedProductIds(finalSelectedProductIds);

        try {
          await fetchWithTokenAndRegion(putUserPreferenceUrl(project.id), {
            method: 'PUT',
            headers: {
              'content-type': 'application/json',
            },
            body: JSON.stringify({ value: productId }),
          });

          // Show Success Notification
          showNotification({
            ...SuccessNotificationBaseProps,
            message: (
              <span>
                <Trans
                  i18nKey="PRODUCT_ENTRY_SUCCESS_NOTIFICATION"
                  values={{
                    projectName: project.name,
                    productName,
                  }}
                />
              </span>
            ),
          });
        } catch (error) {
          showNotification({
            ...ErrorNotificationBaseProps,
            message: (
              <span>
                <Trans
                  i18nKey="PRODUCT_ENTRY_ERROR_NOTIFICATION"
                  values={{
                    projectName: project.name,
                    productName,
                  }}
                />
              </span>
            ),
          });
        }
      },

      loadProductSelections: (data?: ProductEntryFlowData[]) => {
        const finalSelectedProductIds: ProductEntryFlowValue = {};
        data?.map(({ id, value }) => (finalSelectedProductIds[id] = value));
        setSelectedProductIds(finalSelectedProductIds);
        return { finalSelectedProductIds, ...selectedProductIds };
      },
    }),
    [
      enableTableProductEntryFlow,
      loading,
      selectedProductIds,
      setSelectedProductIds,
      showNotification,
    ],
  );

  return (
    <ProductEntryFlowContext.Provider value={value}>
      {children}
    </ProductEntryFlowContext.Provider>
  );
};
